import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Search, ArrowLeft, ExternalLink, ArrowUpDown,
  ChevronLeft, ChevronRight, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeStyle = (theme) => {
  const map = { blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', violet: 'bg-violet-50 text-violet-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', sky: 'bg-sky-50 text-sky-700' };
  return map[theme] || 'bg-gray-50 text-gray-600';
};

export default function PartnersSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [sort, setSort] = useState('recommended');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [filters, setFilters] = useState({ featured: searchParams.get('featured') === 'true', member_benefit: false, exclusive: false, verified: false });

  useEffect(() => {
    fetch(`${API}/partners/categories?include_empty=false`).then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedCat) params.set('category', selectedCat);
    if (filters.featured) params.set('featured', 'true');
    if (filters.member_benefit) params.set('member_benefit', 'true');
    if (filters.exclusive) params.set('exclusive', 'true');
    if (filters.verified) params.set('verified', 'true');
    params.set('sort', sort);
    params.set('page', page.toString());
    fetch(`${API}/partners/search?${params}`)
      .then(r => r.json())
      .then(d => { setResults(d.partners || []); setTotal(d.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [query, selectedCat, sort, page, filters]);

  const totalPages = Math.ceil(total / 24);

  const trackClick = (partnerId) => {
    fetch(`${API}/partners/click/${partnerId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_page: 'search' }) }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#fafbfc]" data-testid="partners-search-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-6">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/partners"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Partners</Button></Link>
            <h1 className="text-lg font-bold text-[#0f1f33]">Search Partners</h1>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={query} onChange={e => { setQuery(e.target.value); setPage(1); }} placeholder="Search partners..." className="pl-10 h-10" data-testid="search-input" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Filters */}
          <div className="w-full lg:w-56 flex-shrink-0 space-y-4" data-testid="filters-sidebar">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1"><Filter className="w-3 h-3" /> Category</p>
              <select value={selectedCat} onChange={e => { setSelectedCat(e.target.value); setPage(1); }} className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm">
                <option value="">All Categories</option>
                {categories.map((c, i) => <option key={i} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              {[
                { key: 'featured', label: 'Featured' },
                { key: 'verified', label: 'Verified' },
                { key: 'member_benefit', label: 'AREA Member Benefit' },
                { key: 'exclusive', label: 'Exclusive Discount' },
              ].map(f => (
                <label key={f.key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={filters[f.key]} onChange={e => { setFilters(p => ({ ...p, [f.key]: e.target.checked })); setPage(1); }} className="rounded border-gray-300 w-3.5 h-3.5" />
                  <span className="text-sm text-gray-600">{f.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Results */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-400">{total} result{total !== 1 ? 's' : ''}</p>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                <select value={sort} onChange={e => setSort(e.target.value)} className="text-sm border-gray-200 rounded-md h-8 px-2">
                  <option value="recommended">Recommended</option>
                  <option value="featured">Featured</option>
                  <option value="alphabetical">Alphabetical</option>
                  <option value="newest">Newest</option>
                  <option value="most_clicked">Most Clicked</option>
                </select>
              </div>
            </div>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-gray-50 rounded-xl animate-pulse border" />)}</div>
            ) : results.length === 0 ? (
              <div className="text-center py-16"><Search className="w-10 h-10 text-gray-200 mx-auto mb-3" /><p className="text-sm text-gray-400">No partners found.</p></div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.map((p, i) => (
                  <Card key={i} className="border-gray-100 shadow-sm hover:shadow-lg transition-all group" data-testid={`search-result-${p.slug}`}>
                    <CardContent className="p-4 flex flex-col h-full">
                      {p.logo_url && <img src={p.logo_url} alt={p.name} className="h-8 w-auto object-contain mb-2" />}
                      <h3 className="text-sm font-semibold text-[#0f1f33] group-hover:text-[#2563eb] mb-1">{p.name}</h3>
                      <p className="text-xs text-gray-500 line-clamp-2 mb-2 flex-1">{p.short_description}</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {p.badges?.map((b, j) => <Badge key={j} className={`text-[10px] ${badgeStyle(b.color_theme)}`}>{b.label}</Badge>)}
                      </div>
                      {p.discount_text && <p className="text-xs text-emerald-600 font-medium mb-2">{p.discount_text}</p>}
                      <div className="flex gap-2 mt-auto">
                        <Link to={`/partners/${p.slug}`} className="flex-1"><Button variant="outline" size="sm" className="w-full text-xs">Details</Button></Link>
                        {p.affiliate_url && (
                          <a href={p.affiliate_url} target="_blank" rel="noopener noreferrer" onClick={() => trackClick(p.partner_id)} className="flex-1">
                            <Button size="sm" className="w-full bg-[#c41230] hover:bg-[#a00f28] text-white text-xs">{p.primary_cta_text || 'Visit'} <ExternalLink className="w-3 h-3 ml-1" /></Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-6">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
